// Recidiviz - a data platform for criminal justice reform
// Copyright (C) 2024 Recidiviz, Inc.
//
// This program is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as published by
// the Free Software Foundation, either version 3 of the License, or
// (at your option) any later version.
//
// This program is distributed in the hope that it will be useful,
// but WITHOUT ANY WARRANTY; without even the implied warranty of
// MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
// GNU General Public License for more details.
//
// You should have received a copy of the GNU General Public License
// along with this program.  If not, see <https://www.gnu.org/licenses/>.
// =============================================================================

import { autorun, runInAction } from "mobx";
import { observer } from "mobx-react-lite";
import React, { useEffect, useState } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";

import { SystemId } from "~datatypes";

import NotFound from "../../components/NotFound";
import { useRootStore } from "../../components/StoreProvider";
import { WorkflowsRouteParams } from "../../WorkflowsStore";
import { getSystemIdFromPage } from "../../WorkflowsStore/utils";
import {
  WORKFLOWS_PATHS_WITHOUT_OPP_ID,
  WORKFLOWS_PATHS_WITHOUT_PERSON_ID,
  WorkflowsPathSection,
  workflowsUrl,
} from "../views";

// react-router does not seem to export this type directly
export type RouterLocation = ReturnType<typeof useLocation>;

export function parseLocation(loc: RouterLocation): WorkflowsRouteParams {
  // slicing off empty string at 0 caused by leading slash,
  // and 1 which should always be "workflows"
  const pathContents: Array<string | undefined> = loc.pathname
    .split("/")
    .slice(2);

  const page = pathContents[0];

  const canHavePersonId =
    page && !WORKFLOWS_PATHS_WITHOUT_PERSON_ID.includes(page);
  const personId = canHavePersonId ? pathContents[1] : undefined;

  const canHaveOppId = page && !WORKFLOWS_PATHS_WITHOUT_OPP_ID.includes(page);
  const opportunityPseudoId = canHaveOppId ? pathContents[2] : undefined;

  return { page, personId, opportunityPseudoId };
}

const RouteSync = observer(function RouteSync({
  children,
}: {
  children?: React.ReactNode;
}) {
  const {
    workflowsStore,
    currentTenantId,
    workflowsRootStore: { opportunityConfigurationStore },
  } = useRootStore();
  const loc = useLocation();

  const [notFound, setNotFound] = useState(false);
  const [redirectPath, setRedirectPath] = useState<string | undefined>();

  const [previousLocContainedPersonId, setPreviousLocContainedPersonId] =
    useState(false);
  const [
    previousLocContainedOpportunityId,
    setPreviousLocContainedOpportunityId,
  ] = useState(false);

  useEffect(
    () =>
      autorun(() => {
        const { page, personId, opportunityPseudoId } = parseLocation(loc);
        const { workflowsSupportedSystems, homepage } = workflowsStore;
        setRedirectPath(undefined);
        workflowsStore.setActivePage({ page, personId });

        const opportunityType =
          page && opportunityConfigurationStore.getOpportunityTypeFromUrl(page);

        /* 1. Update activeSystem and selectedOpportunityType */
        if (opportunityType) {
          workflowsStore.updateActiveSystem(
            opportunityConfigurationStore.opportunities[opportunityType]
              .systemType,
          );
          workflowsStore.updateSelectedOpportunityType(opportunityType);
        } else if (
          page === "home" &&
          // Read the feature variant via the MobX-observable getter on UserStore,
          // NOT via the React `useFeatureVariants()` hook. The autorun isn't a
          // hook context; the getter is observable so the autorun re-runs if
          // the flag value changes.
          workflowsStore.rootStore.userStore.activeFeatureVariants
            .usMoMyCaseload
        ) {
          // MyCaseload is supervision-only. The default page-to-system mapping
          // puts "home" in the ALL bucket; override here so CaseloadSelect
          // renders as officer-search instead of falling back to "caseload"
          // labelling.
          workflowsStore.updateActiveSystem("SUPERVISION");
          workflowsStore.updateSelectedOpportunityType(undefined);
        } else {
          // Select active system from the page type or take the first supported system available
          const activeSystem: SystemId | undefined =
            workflowsSupportedSystems &&
            workflowsSupportedSystems?.length > 1 &&
            page
              ? getSystemIdFromPage(page as WorkflowsPathSection)
              : workflowsSupportedSystems?.[0];

          if (activeSystem) workflowsStore.updateActiveSystem(activeSystem);
          workflowsStore.updateSelectedOpportunityType(undefined);
        }

        /* 2. Update selectedPerson if there is a personId, and selectedOpportunity if
          there is an opportunityId */
        // updateSelectedPerson relies on the active system, so set it after the above
        if (personId || previousLocContainedPersonId) {
          workflowsStore.updateSelectedPerson(personId).catch(() => {
            if (opportunityType) {
              // Redirect home if person is no long eligible for opportunity
              setRedirectPath(workflowsUrl(homepage));
            } else {
              setNotFound(true);
            }
          });
        }

        if (opportunityPseudoId || previousLocContainedOpportunityId) {
          workflowsStore.updateSelectedOpportunity(opportunityPseudoId);
        }

        setPreviousLocContainedPersonId(!!personId);
        setPreviousLocContainedOpportunityId(!!opportunityPseudoId);

        /* 3. Redirect to first available opportunity page if only 1 opportunity or homepage for multiple */
        if (!page) {
          // we aren't actually mutating any observables here,
          // but we just don't want the access tracked in this effect
          // (it is mixing observable and unobservable dependencies, which could lead
          // to unpredictable or undesirable re-renders)
          runInAction(() => {
            const { opportunityTypes } = workflowsStore;
            if (opportunityTypes.length === 1) {
              const { urlSection } =
                opportunityConfigurationStore.opportunities[
                  opportunityTypes[0]
                ];
              setRedirectPath(
                workflowsUrl("opportunityClients", {
                  urlSection,
                }),
              );
            } else {
              setRedirectPath(workflowsUrl(homepage));
            }
          });
        }
      }),
    [
      loc,
      opportunityConfigurationStore,
      workflowsStore,
      currentTenantId,
      previousLocContainedPersonId,
      previousLocContainedOpportunityId,
    ],
  );

  if (notFound) {
    return <NotFound />;
  }
  if (redirectPath) {
    return <Navigate replace to={redirectPath} />;
  }
  return <>{children}</>;
});

/**
 * Wraps a react-router Route to sync route data to the Workflows datastore.
 */
const WorkflowsRoute: React.FC = () => {
  return (
    <RouteSync>
      <Outlet />
    </RouteSync>
  );
};

export default WorkflowsRoute;
